import { create } from 'zustand';

/**
 * Connectivity store — the console's view of its two links to the hub
 * (2026-09 disconnect early warning; spec
 * `docs/specs/2026-09-features/01-disconnect-early-warning.md` §3.5, §3.6).
 *
 *   - `hubUnreachable` — can the REST API be reached at all? Fed by the
 *     axios response interceptor (T1-Fa, `lib/api-client.ts`):
 *     `connectivity.markHubUnreachable(kind)` when `isHubUnreachableError`
 *     matches, `connectivity.markHubReachable()` on any response that did
 *     come from the hub. The 401 path is untouched by this store.
 *   - `ws` — the realtime socket's lifecycle. Fed by `useRealtime.ts`
 *     (T1-Fa) from the connection's `onOpen` / `onClose` callbacks through
 *     `connectivity.setWs(...)`.
 *
 * Consumers (T1-Fb / T1-Fa): the app-shell `FleetStatusBanner` (hub
 * unreachable → red; WS reconnecting for more than
 * {@link WS_RECONNECTING_GRACE_MS} → amber), `useDocumentTitleAlert` (`⚠ `
 * title prefix while the hub is unreachable) and the `HubOutageScreen`.
 *
 * Client state, so zustand (CLAUDE.md: Zustand for client state). The two
 * feeders run OUTSIDE React (an axios interceptor, WebSocket callbacks), so
 * they call the plain {@link connectivity} facade, which goes through
 * `getState()` — the same pattern as `useAuthStore.getState().clear()` in
 * the 401 path. Timestamps are epoch milliseconds (`Date.now()`).
 */

/**
 * Why the hub is considered unreachable — one value per branch of
 * `isHubUnreachableError(err)` (spec 01 §3.6 item 2):
 *
 *   - `'network'` — no HTTP response at all (offline, DNS / TCP / TLS).
 *   - `'edge'`    — nginx's edge page: the JSON body carries
 *                   `edge: "hub_unreachable"` (a 503 — the hub's upstream
 *                   502/503/504 are rewritten there).
 *   - `'proxy'`   — a 502 / 504 with a NON-JSON body (the dev vite proxy's
 *                   error page).
 *
 * A hub's own JSON 5xx (e.g. a Sheets 502) is NOT unreachable — the hub
 * answered.
 */
export type HubUnreachableKind = 'network' | 'edge' | 'proxy';

/**
 * Lifecycle of the realtime socket, as `useRealtime` observes it:
 *
 *   - `'idle'`         — no connection wanted (signed out / torn down).
 *   - `'connecting'`   — the session's first attempt is in flight; the
 *                        socket has not opened yet.
 *   - `'open'`         — authenticated and live.
 *   - `'reconnecting'` — the socket dropped (or the first attempt failed);
 *                        `connection.ts`'s backoff loop is retrying.
 */
export type WsState = 'idle' | 'connecting' | 'open' | 'reconnecting';

/** Spec 01 §3.5 ("WS reconnecting > 5 s (amber)"): the amber banner waits
 *  this long — a wifi roam or a hub restart blip reconnects inside it and
 *  stays silent. */
export const WS_RECONNECTING_GRACE_MS = 5_000;

export interface HubOutage {
  /** Branch of the MOST RECENT unreachable error. */
  kind: HubUnreachableKind;
  /** When the outage began — the FIRST unreachable error since the hub was
   *  last reachable (later errors do not move it). */
  since: number;
}

export interface ConnectivityState {
  /** `null` = reachable, or not known to be unreachable (initial). */
  hubUnreachable: HubOutage | null;
  ws: WsState;
  /** When `ws` last changed VALUE (`null` before the first `setWs`). A repeat
   *  `setWs('reconnecting')` — every failed backoff attempt closes again —
   *  keeps the original timestamp, so the grace clock is not reset. */
  wsSince: number | null;
  markHubUnreachable: (kind: HubUnreachableKind) => void;
  markHubReachable: () => void;
  setWs: (next: WsState) => void;
  /** Back to the initial state (tests; a full sign-out). */
  reset: () => void;
}

const INITIAL: Pick<ConnectivityState, 'hubUnreachable' | 'ws' | 'wsSince'> = {
  hubUnreachable: null,
  ws: 'idle',
  wsSince: null,
};

export const useConnectivityStore = create<ConnectivityState>((set) => ({
  ...INITIAL,
  markHubUnreachable: (kind) =>
    set((state) => {
      const current = state.hubUnreachable;
      if (current === null) {
        return { hubUnreachable: { kind, since: Date.now() } };
      }
      // Already down: keep the outage's start, refresh only the branch.
      return current.kind === kind ? state : { hubUnreachable: { ...current, kind } };
    }),
  // Called on EVERY successful response — must be a true no-op (same state
  // object → zustand notifies nobody) when the hub was already reachable.
  markHubReachable: () =>
    set((state) => (state.hubUnreachable === null ? state : { hubUnreachable: null })),
  setWs: (next) =>
    set((state) => (state.ws === next ? state : { ws: next, wsSince: Date.now() })),
  reset: () => set({ ...INITIAL }),
}));

/**
 * Plain-function facade for the non-React feeders — the spec's
 * `connectivity.markHubUnreachable(kind)` / `connectivity.markHubReachable()`
 * / `connectivity.setWs(...)`.
 */
export const connectivity = {
  markHubUnreachable: (kind: HubUnreachableKind): void =>
    useConnectivityStore.getState().markHubUnreachable(kind),
  markHubReachable: (): void => useConnectivityStore.getState().markHubReachable(),
  setWs: (next: WsState): void => useConnectivityStore.getState().setWs(next),
  reset: (): void => useConnectivityStore.getState().reset(),
};

/**
 * How long (ms) the socket has been `reconnecting` at `now`; `0` in every
 * other state. The banner compares it with {@link WS_RECONNECTING_GRACE_MS}
 * (and schedules its own re-render for the moment the grace runs out — the
 * store does not tick).
 */
export function wsReconnectingForMs(
  state: Pick<ConnectivityState, 'ws' | 'wsSince'>,
  now: number,
): number {
  if (state.ws !== 'reconnecting' || state.wsSince === null) return 0;
  return Math.max(0, now - state.wsSince);
}
